import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import { posthog } from 'posthog-js';
import { CloudUpload, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { GlobalDialogProps } from '../../types';
import { convertTextToSpeech, callGroqForTTS } from '../microtools/common';
import { uploadFiles } from '../upload/uploadSlice';
import { AppDispatch } from '../../store';

// Configure PDF.js worker
if (typeof window !== 'undefined') {
  pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
}

const ACCEPTED_FILE_TYPES = ['application/pdf', 'text/plain'];

const ACCEPTED_EXTENSIONS = '.pdf,.txt';

// Thresholds for when to summarize vs use original text
const SUMMARY_CHAR_THRESHOLD = 5000;
const SUMMARY_PAGE_THRESHOLD = 5;
const MAX_TEXT_INPUT_LENGTH = 5000;
const MAX_TTS_BYTES = 3800; // Google TTS limit is 4000 bytes, leave buffer

// Step types for the conversion process
type TTSStep =
  | 'idle'
  | 'extracting'
  | 'summarizing'
  | 'generating'
  | 'saving'
  | 'complete';

interface TextToSpeechState {
  isLoading: boolean;
  loadingMessage: string;
  error: string | null;
  audioUrl: string | null;
  audioBlob: Blob | null;
  uploading: boolean;
  progress: number;
  uploadComplete: boolean;
  currentStep: TTSStep;
  willSummarize: boolean;
  isFileUpload: boolean;
}

const truncateToByteLimit = (text: string, maxBytes: number): string => {
  const encoder = new TextEncoder();
  const encoded = encoder.encode(text);
  if (encoded.length <= maxBytes) {
    return text;
  }
  // Truncate and find last complete sentence
  const truncated = new TextDecoder().decode(encoded.slice(0, maxBytes));
  const lastSentenceEnd = Math.max(
    truncated.lastIndexOf('. '),
    truncated.lastIndexOf('! '),
    truncated.lastIndexOf('? '),
  );
  if (lastSentenceEnd > maxBytes * 0.5) {
    return truncated.slice(0, lastSentenceEnd + 1);
  }
  return truncated.slice(0, truncated.lastIndexOf(' ')) || truncated;
};

const cleanTextForTTS = (text: string): string => {
  return text
    .replace(/[^\w\s.,!?;:()\-"']/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\.{2,}/g, '.')
    .replace(/!{2,}/g, '!')
    .replace(/\?{2,}/g, '?')
    .replace(/\b[\d\W]{1,2}\b/g, ' ')
    .replace(/\s+([,.!?;:])/g, '$1')
    .replace(/([,.!?;:])\s+/g, '$1 ')
    .replace(/[,.!?;:]{3,}/g, '.')
    .replace(/["'""`´]/g, '"')
    .replace(/[''`]/g, "'")
    .replace(/https?:\/\/[^\s]+/g, 'website')
    .replace(
      /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
      'email address',
    )
    .replace(/\s{2,}/g, ' ')
    .trim();
};

const createPodcastSummary = async (text: string): Promise<string> => {
  const wordCount = text.split(/\s+/).length;
  const adaptiveMinWords = Math.max(300, Math.floor(wordCount * 0.15));
  const adaptiveMaxWords = Math.max(2000, Math.floor(wordCount * 0.4));
  const maxCharacters = 8000;

  const prompt = `Transform this document into a clear, well-structured podcast narrative optimized for text-to-speech conversion.

REQUIREMENTS:
- Length: ${adaptiveMinWords}-${adaptiveMaxWords} words (maximum ${maxCharacters} characters)
- Structure: Introduction, main content, conclusion
- Tone: Professional and conversational
- Style: Clear language, logical flow, 15-25 words per sentence
- Keep in Original language

OUTPUT RULES:
- Provide ONLY clean narrative content suitable for audio
- Do NOT include meta-commentary, citations, or references
- Do NOT mention this is a summary or transformation
- Do NOT use special characters, symbols, or complex formatting
- Start directly with the content
- Use simple punctuation: periods, commas, question marks, exclamation points
- Avoid parentheses, brackets, slashes, or complex punctuation

CONTENT GUIDELINES:
- Begin with brief context about the main topic
- Present key points in logical sequence using simple sentences
- Focus on essential information and insights
- End with concise recap and key takeaways
- Convert lists and tables into flowing narrative sentences
- Spell out acronyms fully on first use
- Use clear number formats spelled out when possible
- Avoid technical jargon, use everyday language
- Make it sound natural when spoken aloud

TEXT-TO-SPEECH OPTIMIZATION:
- Use conversational transitions like "Now," "Next," "Additionally"
- Avoid complex compound sentences
- Use active voice
- Keep sentences under 25 words
- Use simple, common words

SOURCE DOCUMENT:
${text}`;

  const rawSummary = await callGroqForTTS(prompt);
  const cleanedSummary = cleanTextForTTS(rawSummary);

  if (!cleanedSummary || cleanedSummary.length < 50) {
    throw new Error('Generated summary too short or empty after cleaning');
  }

  return cleanedSummary;
};

interface PDFExtractionResult {
  text: string;
  pageCount: number;
}

const extractTextFromPDF = async (
  file: File,
  onProgress: (message: string) => void,
): Promise<PDFExtractionResult> => {
  onProgress('Reading PDF file...');
  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({
    data: arrayBuffer,
    useWorkerFetch: false,
    isEvalSupported: false,
    useSystemFonts: true,
  });
  const pdf = await loadingTask.promise;

  onProgress(`Extracting text from ${pdf.numPages} pages...`);

  const pagePromises = Array.from(
    { length: pdf.numPages },
    async (_, index) => {
      const pageNum = index + 1;
      onProgress(`Processing page ${pageNum} of ${pdf.numPages}...`);

      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();

      if (!textContent.items.length) {
        return '';
      }

      const textItems = textContent.items
        .filter(
          (item): item is any =>
            'str' in item &&
            typeof item.str === 'string' &&
            Boolean(item.str.trim()),
        )
        .map((item: any) => ({
          str: item.str,
          x: item.transform[4],
          y: item.transform[5],
          width: item.width,
          height: item.height,
        }));

      if (textItems.length === 0) {
        return '';
      }

      textItems.sort((a, b) => {
        const yTolerance = Math.max(a.height, b.height) * 0.3;
        const yDiff = b.y - a.y;

        if (Math.abs(yDiff) > yTolerance) {
          return yDiff > 0 ? 1 : -1;
        }
        return a.x - b.x;
      });

      const result = textItems.reduce(
        (acc, item) => {
          const text = item.str.trim();
          if (!text) return acc;

          const { pageText, lastItem } = acc;

          if (lastItem) {
            const yTolerance = Math.max(lastItem.height, item.height) * 0.3;
            const yDiff = Math.abs(item.y - lastItem.y);
            const xGap = item.x - (lastItem.x + lastItem.width);

            if (yDiff > yTolerance) {
              const paragraphGap = Math.max(lastItem.height, item.height) * 1.5;
              if (yDiff > paragraphGap) {
                acc.pageText += '\n\n';
              } else {
                acc.pageText += '\n';
              }
            } else if (xGap > item.height * 0.2 && !pageText.endsWith(' ')) {
              acc.pageText += ' ';
            }
          }

          acc.pageText += text;
          acc.lastItem = item;
          return acc;
        },
        { pageText: '', lastItem: null as (typeof textItems)[0] | null },
      );

      return result.pageText;
    },
  );

  const pageTexts = await Promise.all(pagePromises);
  const pageCount = pdf.numPages;
  await loadingTask.destroy();

  let fullText = pageTexts.filter(Boolean).join('\n\n').trim();

  if (!fullText) {
    throw new Error(
      'No readable text found in the PDF. The file might be scanned or image-based.',
    );
  }

  fullText = fullText
    .replace(/[ \t]+/g, ' ')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/\n{4,}/g, '\n\n\n')
    .replace(/\n{3}/g, '\n\n')
    .replace(/([a-z])-\s*\n\s*([a-z])/gi, '$1$2')
    .replace(/([.!?])\s*\n\s*([A-Z])/g, '$1\n\n$2')
    .replace(/\s+([,.!?;:])/g, '$1')
    .replace(/([,.!?;:])\s+/g, '$1 ')
    .replace(/\n\s*[a-zA-Z]\s*\n/g, '\n')
    .trim();

  return { text: fullText, pageCount };
};

const extractTextFromTXT = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (!text || text.trim().length === 0) {
        reject(new Error('The text file is empty.'));
        return;
      }
      resolve(text.trim());
    };
    reader.onerror = () => reject(new Error('Failed to read text file.'));
    reader.readAsText(file);
  });
};

// Calculate progress percentage based on current step
const getStepProgress = (
  step: TTSStep,
  willSummarize: boolean,
  isFileUpload: boolean,
): number => {
  let steps: string[];

  if (isFileUpload && willSummarize) {
    steps = ['extracting', 'summarizing', 'generating', 'saving', 'complete'];
  } else if (isFileUpload) {
    steps = ['extracting', 'generating', 'saving', 'complete'];
  } else if (willSummarize) {
    steps = ['summarizing', 'generating', 'saving', 'complete'];
  } else {
    steps = ['generating', 'saving', 'complete'];
  }

  const index = steps.indexOf(step);
  if (index === -1) return 0;
  return Math.round((index / (steps.length - 1)) * 100);
};

const TextToSpeechModal: React.FC<GlobalDialogProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { teamId, folderId } = useParams<{
    teamId: string;
    folderId: string;
  }>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [state, setState] = useState<TextToSpeechState>({
    isLoading: false,
    loadingMessage: '',
    error: null,
    audioUrl: null,
    audioBlob: null,
    uploading: false,
    progress: 0,
    uploadComplete: false,
    currentStep: 'idle',
    willSummarize: false,
    isFileUpload: false,
  });

  useEffect(() => {
    return () => {
      if (state.audioUrl) {
        URL.revokeObjectURL(state.audioUrl);
      }
    };
  }, [state.audioUrl]);

  const processFile = useCallback(
    async (file: File) => {
      if (
        !ACCEPTED_FILE_TYPES.includes(file.type) &&
        !file.name.endsWith('.txt')
      ) {
        toast.error(
          t(
            'textToSpeechModal.error.unsupportedFormat',
            'Unsupported file format. Please use PDF or TXT files.',
          ),
        );
        return;
      }

      setState((prev) => ({
        ...prev,
        isLoading: true,
        loadingMessage: t('textToSpeechModal.extracting', 'Extracting text...'),
        error: null,
        audioUrl: null,
        audioBlob: null,
        currentStep: 'extracting',
        willSummarize: false,
        isFileUpload: true,
      }));

      // Track file upload start
      const fileType =
        file.type === 'application/pdf' || file.name.endsWith('.pdf')
          ? 'pdf'
          : 'txt';
      posthog.capture('tts-started', {
        inputType: 'file',
        fileType,
        fileName: file.name,
        fileSizeBytes: file.size,
        timestamp: new Date().toISOString(),
      });

      try {
        let text: string;
        let pageCount = 0;

        if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
          const pdfResult = await extractTextFromPDF(file, (message) => {
            setState((prev) => ({ ...prev, loadingMessage: message }));
          });
          text = pdfResult.text;
          pageCount = pdfResult.pageCount;
        } else {
          text = await extractTextFromTXT(file);
        }

        // Decide whether to summarize based on content size
        const shouldSummarize =
          text.length > SUMMARY_CHAR_THRESHOLD ||
          pageCount > SUMMARY_PAGE_THRESHOLD;

        // Update willSummarize so the step indicator shows the correct steps
        setState((prev) => ({
          ...prev,
          willSummarize: shouldSummarize,
        }));

        let audioText: string;

        if (shouldSummarize) {
          setState((prev) => ({
            ...prev,
            currentStep: 'summarizing',
            loadingMessage: t(
              'textToSpeechModal.processing',
              'Processing your document...',
            ),
          }));

          audioText = await createPodcastSummary(text);
        } else {
          // For short content, just clean the text for TTS
          audioText = cleanTextForTTS(text);
        }

        setState((prev) => ({
          ...prev,
          currentStep: 'generating',
          loadingMessage: t(
            'textToSpeechModal.converting',
            'Converting to audio...',
          ),
        }));

        // Truncate to TTS byte limit
        const truncatedText = truncateToByteLimit(audioText, MAX_TTS_BYTES);
        const blob = await convertTextToSpeech(truncatedText);
        const url = URL.createObjectURL(blob);

        setState((prev) => ({
          ...prev,
          audioUrl: url,
          audioBlob: blob,
          isLoading: false,
          loadingMessage: '',
        }));

        // Upload directly to library using uploadFiles thunk
        if (!teamId || !folderId) {
          toast.error(
            t(
              'textToSpeechModal.error.generic',
              'Failed to process. Please try again.',
            ),
          );
          return;
        }

        setState((prev) => ({
          ...prev,
          uploading: true,
          currentStep: 'saving',
          loadingMessage: t('textToSpeechModal.uploading', 'Saving audio...'),
        }));

        // Convert blob to File object
        const audioFileName = `${file.name.replace(/\.[^/.]+$/, '')}-audio.mp3`;
        const audioFile = new File([blob], audioFileName, {
          type: 'audio/mpeg',
        });

        try {
          await dispatch(uploadFiles({ teamId, folderId, files: [audioFile] }));
          setState((prev) => ({
            ...prev,
            uploadComplete: true,
            uploading: false,
            currentStep: 'complete',
          }));

          // Track success
          posthog.capture('tts-completed', {
            inputType: 'file',
            fileType,
            fileName: file.name,
            fileSizeBytes: file.size,
            pageCount,
            originalTextLength: text.length,
            wasSummarized: shouldSummarize,
            timestamp: new Date().toISOString(),
          });

          toast.success(
            t(
              'textToSpeechModal.success',
              'Audio generated and saved successfully!',
            ),
          );
          onClose();
        } catch (uploadErr) {
          const uploadErrorMessage =
            uploadErr instanceof Error ? uploadErr.message : 'Upload failed';

          // Track upload error
          posthog.capture('tts-error', {
            inputType: 'file',
            fileType,
            fileName: file.name,
            errorType: 'upload',
            errorMessage: uploadErrorMessage,
            timestamp: new Date().toISOString(),
          });

          setState((prev) => ({
            ...prev,
            error: uploadErrorMessage,
            uploading: false,
          }));
          toast.error(uploadErrorMessage);
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : t(
                'textToSpeechModal.error.generic',
                'Failed to process. Please try again.',
              );

        // Track processing error
        posthog.capture('tts-error', {
          inputType: 'file',
          fileType,
          fileName: file.name,
          errorType: 'processing',
          errorMessage,
          timestamp: new Date().toISOString(),
        });

        setState((prev) => ({
          ...prev,
          error: errorMessage,
          isLoading: false,
          loadingMessage: '',
        }));
        toast.error(errorMessage);
      }
    },
    [t, onClose, dispatch, teamId, folderId],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        processFile(files[0]);
      }
    },
    [processFile],
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length > 0) {
        processFile(files[0]);
      }
    },
    [processFile],
  );

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const processTextInput = useCallback(async () => {
    const trimmedText = textInput.trim();
    if (!trimmedText) {
      toast.error(
        t('textToSpeechModal.error.emptyText', 'Please enter some text.'),
      );
      return;
    }

    const willBeSummarized = trimmedText.length > MAX_TEXT_INPUT_LENGTH;

    setState((prev) => ({
      ...prev,
      isLoading: true,
      loadingMessage: t(
        'textToSpeechModal.converting',
        'Converting to audio...',
      ),
      error: null,
      audioUrl: null,
      audioBlob: null,
      currentStep: willBeSummarized ? 'summarizing' : 'generating',
      willSummarize: willBeSummarized,
      isFileUpload: false,
    }));

    // Track text input start
    posthog.capture('tts-started', {
      inputType: 'text',
      textLength: trimmedText.length,
      willBeSummarized,
      timestamp: new Date().toISOString(),
    });

    try {
      let audioText: string;

      // If text exceeds threshold, summarize it first
      if (willBeSummarized) {
        setState((prev) => ({
          ...prev,
          currentStep: 'summarizing',
          loadingMessage: t(
            'textToSpeechModal.processing',
            'Processing your document...',
          ),
        }));
        audioText = await createPodcastSummary(trimmedText);

        // Move to generating step after summarization
        setState((prev) => ({
          ...prev,
          currentStep: 'generating',
          loadingMessage: t(
            'textToSpeechModal.converting',
            'Converting to audio...',
          ),
        }));
      } else {
        audioText = cleanTextForTTS(trimmedText);
      }

      // Truncate to TTS byte limit
      const truncatedText = truncateToByteLimit(audioText, MAX_TTS_BYTES);
      const blob = await convertTextToSpeech(truncatedText);
      const url = URL.createObjectURL(blob);

      setState((prev) => ({
        ...prev,
        audioUrl: url,
        audioBlob: blob,
        isLoading: false,
        loadingMessage: '',
      }));

      if (!teamId || !folderId) {
        toast.error(
          t(
            'textToSpeechModal.error.generic',
            'Failed to process. Please try again.',
          ),
        );
        return;
      }

      setState((prev) => ({
        ...prev,
        uploading: true,
        currentStep: 'saving',
        loadingMessage: t('textToSpeechModal.uploading', 'Saving audio...'),
      }));

      const timestamp = Date.now();
      const audioFileName = `text-to-speech-${timestamp}.mp3`;
      const audioFile = new File([blob], audioFileName, { type: 'audio/mpeg' });

      try {
        await dispatch(uploadFiles({ teamId, folderId, files: [audioFile] }));
        setState((prev) => ({
          ...prev,
          uploadComplete: true,
          uploading: false,
          currentStep: 'complete',
        }));

        // Track success
        posthog.capture('tts-completed', {
          inputType: 'text',
          textLength: trimmedText.length,
          wasSummarized: willBeSummarized,
          timestamp: new Date().toISOString(),
        });

        toast.success(
          t(
            'textToSpeechModal.success',
            'Audio generated and saved successfully!',
          ),
        );
        onClose();
      } catch (uploadErr) {
        const uploadErrorMessage =
          uploadErr instanceof Error ? uploadErr.message : 'Upload failed';

        // Track upload error
        posthog.capture('tts-error', {
          inputType: 'text',
          textLength: trimmedText.length,
          errorType: 'upload',
          errorMessage: uploadErrorMessage,
          timestamp: new Date().toISOString(),
        });

        setState((prev) => ({
          ...prev,
          error: uploadErrorMessage,
          uploading: false,
        }));
        toast.error(uploadErrorMessage);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : t(
              'textToSpeechModal.error.generic',
              'Failed to process. Please try again.',
            );

      // Track processing error
      posthog.capture('tts-error', {
        inputType: 'text',
        textLength: trimmedText.length,
        errorType: 'processing',
        errorMessage,
        timestamp: new Date().toISOString(),
      });

      setState((prev) => ({
        ...prev,
        error: errorMessage,
        isLoading: false,
        loadingMessage: '',
      }));
      toast.error(errorMessage);
    }
  }, [textInput, t, onClose, dispatch, teamId, folderId]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t('textToSpeechModal.title', 'Text to Speech')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'textToSpeechModal.description',
              'Upload a document to convert to audio',
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* File Upload Box - Compact */}
          <div
            role="button"
            tabIndex={0}
            className={cn(
              'flex flex-col items-center justify-center rounded-lg p-4 transition-colors',
              state.isLoading || state.uploading
                ? 'pointer-events-none'
                : 'cursor-pointer border-2 border-dashed',
              !state.isLoading && !state.uploading && isDragging
                ? 'border-primary bg-primary/5'
                : !state.isLoading &&
                    !state.uploading &&
                    'border-muted-foreground/25 hover:border-primary hover:bg-accent',
            )}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={handleUploadClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleUploadClick();
              }
            }}
          >
            {state.isLoading || state.uploading ? (
              <div className="flex flex-col items-center gap-4 py-6 w-full">
                <Progress
                  value={getStepProgress(
                    state.currentStep,
                    state.willSummarize,
                    state.isFileUpload,
                  )}
                  className="w-full"
                />
                <div className="flex items-center gap-2">
                  <Loader className="h-4 w-4 animate-spin text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {state.uploading
                      ? t('textToSpeechModal.uploading', 'Saving audio...')
                      : state.loadingMessage}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <CloudUpload
                  className={cn(
                    'h-6 w-6',
                    isDragging ? 'text-primary' : 'text-muted-foreground',
                  )}
                />
                <div className="flex flex-col">
                  <p className="text-sm font-medium">
                    {isDragging
                      ? t(
                          'textToSpeechModal.upload.dragging',
                          'Drop your document here',
                        )
                      : t(
                          'textToSpeechModal.upload.idle',
                          'Click to upload or drag and drop',
                        )}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t(
                      'textToSpeechModal.upload.supportedFormats',
                      'Supports PDF and TXT files',
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_EXTENSIONS}
            className="hidden"
            onChange={handleFileSelect}
          />

          {!(state.isLoading || state.uploading) && (
            <>
              <p className="text-xs text-muted-foreground text-center">
                {t(
                  'textToSpeechModal.summarizeNote',
                  'Larger documents will be summarized for optimal audio output',
                )}
              </p>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 border-t border-muted-foreground/25" />
                <span className="text-xs text-muted-foreground">
                  {t('textToSpeechModal.or', 'or')}
                </span>
                <div className="flex-1 border-t border-muted-foreground/25" />
              </div>

              {/* Text Input */}
              <div className="space-y-2">
                <textarea
                  className="w-full h-32 p-3 text-sm rounded-lg border border-muted-foreground/25 bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder={t(
                    'textToSpeechModal.textInput.placeholder',
                    'Enter text to convert to audio...',
                  )}
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">
                    {textInput.length}{' '}
                    {t('textToSpeechModal.textInput.characters', 'characters')}
                    {textInput.length > MAX_TEXT_INPUT_LENGTH && (
                      <span className="ml-1 text-primary">
                        (
                        {t(
                          'textToSpeechModal.textInput.willBeSummarized',
                          'will be summarized',
                        )}
                        )
                      </span>
                    )}
                  </p>
                  <Button
                    size="sm"
                    onClick={processTextInput}
                    disabled={!textInput.trim()}
                  >
                    {t(
                      'textToSpeechModal.textInput.generate',
                      'Generate Audio',
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}

          {state.error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {state.error}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TextToSpeechModal;
